<template>
    <div class="">
        <!-- Hero Section -->
        <div class="mb-6 text-center">
            <h2 class="text-2xl md:text-3xl font-semibold text-gray-800">
                Tìm giá tốt – Đặt xe nhanh – Giao hàng thuận tiện
            </h2>
            <p class="text-gray-500 mt-2 text-sm md:text-base">
                Điền thông tin, nhận ngay báo giá & đặt xe nhanh chóng
            </p>
        </div>

        <!-- Form Section -->
        <div class="space-y-6">
            <!-- Shipment Type -->
            <div>
                <p class="font-bold flex gap-2 items-center">
                    <hi-globe-alt-icon class="w-5 h-5 md:w-6 md:h-6" />
                    <span>
                        Loại hình vận chuyển
                    </span>
                </p>
                <hts-form-radio
                    v-model="form.shipmentType"
                    label=""
                    :items="shipmentTypes"
                    value-key="value"
                    label-key="label"
                    required />
            </div>

            <!-- Transportation -->
            <div>
                <p class="font-bold flex gap-2 items-center">
                    <hi-truck-icon class="w-5 h-5 md:w-6 md:h-6" />
                    <span>
                        Phương tiện vận chuyển
                    </span>
                </p>
                <hts-form-radio
                    v-model="form.transportation"
                    label=""
                    :items="transportations"
                    value-key="value"
                    label-key="label"
                    required />
            </div>

            <!-- Main Form -->
            <hts-form
                class="space-y-6"
                @submit="handleSearch">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <!-- Origin -->
                    <div>
                        <p class="font-bold flex gap-2 items-center mb-2">
                            <hi-map-pin-icon class="h-5 w-5 md:h-6 md:w-6 text-success-600" />
                            <span>
                                Chọn điểm đi (tỉnh/thành phố hoặc cảng)
                            </span>
                        </p>
                        <hts-select-system-node-location
                            v-model="form.origin"
                            v-model:name="form.originName"
                            required
                            class="w-full"
                            label=""
                            :api="apiNode"
                            placeholder="Nhập tỉnh/thành phố hoặc cảng xuất phát"
                            @change="(data) => locationChange('originInfo', data)" />
                    </div>

                    <!-- Destination -->
                    <div>
                        <p class="font-bold flex gap-2 items-center mb-2">
                            <hi-map-pin-icon class="h-5 w-5 md:h-6 md:w-6 text-error-600" />
                            <span>
                                Chọn điểm đến (tỉnh/thành phố hoặc cảng)
                            </span>
                        </p>
                        <hts-select-system-node-location
                            v-model="form.destination"
                            v-model:name="form.destinationName"
                            required
                            class="w-full"
                            label=""
                            :api="apiNode"
                            placeholder="Nhập tỉnh/thành phố hoặc cảng đích"
                            @change="(data) => locationChange('destinationInfo', data)" />
                    </div>
                </div>

                <!-- Container Type / Vehicle Category -->
                <div v-if="form.transportation === 'CONTAINER'">
                    <p class="font-bold flex gap-2 items-center mb-2">
                        <hi-rectangle-stack-icon class="h-5 w-5 md:h-6 md:w-6" />
                        <span>
                            Chọn loại container phù hợp
                        </span>
                    </p>
                    <hts-form-select
                        v-model="form.containerType"
                        class="w-full"
                        :items="containerTypes"
                        key-id="value"
                        required
                        placeholder="Chọn loại container"
                        key-label="label" />
                </div>

                <div v-if="form.transportation === 'NON_CONTAINER'">
                    <p class="font-bold flex gap-2 items-center mb-2">
                        <hi-truck-icon class="h-5 w-5 md:h-6 md:w-6" />
                        <span>
                            Chọn loại xe phù hợp
                        </span>
                    </p>
                    <hts-form-select
                        v-model="form.vehicleCategory"
                        class="w-full"
                        :items="vehicleCategories"
                        key-id="value"
                        required
                        placeholder="Chọn loại xe"
                        key-label="label" />
                </div>

                <!-- Date -->
                <div>
                    <p class="font-bold flex gap-2 items-center mb-2">
                        <hi-calendar-days-icon class="h-5 w-5 md:h-6 md:w-6" />
                        <span>
                            Ngày bắt đầu vận chuyển
                        </span>
                    </p>
                    <hts-form-date
                        v-model="form.date"
                        class="w-full"
                        type="date" />
                </div>

                <hts-form-button
                    :loading="loadingSearch"
                    class="w-full md:w-auto"
                    type="submit"
                    :outline="false"
                    color="primary"
                    icon="magnifying-glass">
                    Tra cứu chuyến xe
                </hts-form-button>
            </hts-form>
        </div>

        <!-- Results -->
        <div
            v-if="Array.isArray(resultPrices)"
            v-loading="loadingSearch"
            class="my-10 space-y-6">
            <div
                v-if="resultPrices.length === 0"
                class="text-center text-gray-500 text-lg">
                Không tìm thấy kết quả phù hợp
            </div>

            <div
                v-for="resultPrice in resultPrices"
                :key="resultPrice"
                class="bg-white shadow-md rounded-xl p-4 md:p-6 space-y-4 border border-gray-200 transition hover:shadow-lg">
                <!-- Main Info -->
                <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h4 class="text-lg md:text-xl font-semibold text-gray-800">
                            {{
                                resultPrice.transportation === 'CONTAINER'
                                    ? getLabel(containerTypes, resultPrice.containerType)
                                    : getLabel(vehicleCategories, resultPrice.vehicleCategory)
                            }}
                        </h4>
                        <p class="text-gray-500 text-sm">
                            Quãng đường: {{ resultPrice.assumption?.totalKm }} Km
                        </p>
                    </div>

                    <div class="text-right">
                        <div class="text-xl md:text-2xl font-bold text-blue-600">
                            {{ $formatters.formatCurrency(resultPrice.price) }} VNĐ
                        </div>
                        <hts-form-button
                            color="info"
                            :outline="false"
                            class="mt-2 w-full md:w-auto"
                            @click="openBookingDrawer(resultPrice)">
                            Book
                        </hts-form-button>
                    </div>
                </div>

                <!-- From - To -->
                <div class="flex flex-col sm:flex-row items-center justify-between text-sm text-gray-600 gap-2">
                    <div class="flex items-center">
                        <hi-map-pin-icon class="h-5 w-5 text-success-600" />
                        <span>
                            {{ resultPrice.originName }}
                        </span>
                    </div>
                    <div class="hidden sm:block flex-1 border-t mx-3 border-gray-300" />
                    <div class="flex items-center">
                        <hi-map-pin-icon class="h-5 w-5 text-error-600" />
                        <span>
                            {{ resultPrice.destinationName }}
                        </span>
                    </div>
                </div>

                <!-- Tooltip -->
                <div class="flex items-start gap-2 text-sm text-gray-700">
                    <p class="font-medium">
                        Thông tin tạo giá:
                    </p>
                    <hts-tooltip placement="top">
                        <template #reference>
                            <hi-exclamation-circle-icon class="h-5 w-5 text-green-500 cursor-pointer" />
                        </template>
                        <div class="bg-white border border-gray-200 shadow-lg rounded-lg p-4 max-w-sm space-y-3">
                            <p>
                                Quãng đường: <span class="font-semibold">
                                    {{ resultPrice?.distance }} Km
                                </span>
                            </p>
                            <p class="font-medium">
                                Tác vụ:
                            </p>
                            <ul class="list-disc list-inside text-gray-800 space-y-1">
                                <li
                                    v-for="(task, taskIndex) in resultPrice.tasks"
                                    :key="taskIndex"
                                    class="flex items-start gap-2">
                                    <hi-check-circle-icon class="h-4 w-4 text-green-500 mt-1" />
                                    <span>
                                        {{ task.taskName }} (<span class="font-medium">
                                            {{ task.timeComplete }} giờ
                                        </span>)
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </hts-tooltip>
                </div>
            </div>
        </div>

        <!-- Lich su tim kiem -->
        <div
            v-if="recentSearches.length && !resultPrices?.length"
            class="p-4">
            <h3 class="text-lg font-semibold mb-4">
                Lịch sử tìm kiếm gần đây
            </h3>

            <ul class="space-y-4">
                <li
                    v-for="(item, index) in recentSearches"
                    :key="index"
                    class="p-4 border border-gray-200 rounded-lg shadow-sm cursor-pointer hover:bg-gray-50 transition"
                    @click="selectSearchHistory(item)">
                    <div class="text-sm text-gray-600">
                        <span class="font-medium text-gray-800">
                            Điểm đi:
                        </span>
                        {{ item.originInfo?.address || 'Không rõ' }}
                    </div>
                    <div class="text-sm text-gray-600 mt-1">
                        <span class="font-medium text-gray-800">
                            Điểm đến:
                        </span>
                        {{ item.destinationInfo?.address || 'Không rõ' }}
                    </div>
                    <div class="text-sm text-gray-600 mt-1">
                        <span class="font-medium text-gray-800">
                            Loại vận chuyển:
                        </span>
                        {{ item.transportation }} - {{ getVehicleType(item) }}
                    </div>
                </li>
            </ul>
        </div>

        <!-- Booking Drawer -->
        <BookingDrawer
            ref="bookingDrawer"
            :type="type" />
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { reactive, ref } from 'vue'
import BookingDrawer from './ignore/booking.vue'
import containerTypes from '~/data/select/containerTypes.json'
import shipmentTypes from '~/data/select/shipmentTypes.json'
import transportations from '~/data/select/transportations.json'
import vehicleCategories from '~/data/select/vehicleCategories.json'

const { $formatters } = useNuxtApp()
const store = $store()

// Refs and reactive state
const loadingSearch = ref<boolean>(false)
const bookingDrawer = ref()
const recentSearches = ref<{ [key: string]: any }[]>([])
const resultPrices = ref<any[] | null>(null)
let observer: ResizeObserver

// Props
const props = defineProps({
  type: {
    type: String,
    default: 'user' // user | guest |
  }
})
const { type } = toRefs(props)

// Computed
const route = useRoute()
const queryGuestId = computed(() => route.query.guestId as string)
const apiNode = computed(() => {
  if (type.value === 'user') {
    return { method: 'get', url: '/admincp/node/location/finds' }
  }
  if (type.value === 'guest') {
    return { method: 'get', url: '/guest/node/location/finds' }
  }
})
const apiSearchBooking = computed(() => {
  if (type.value === 'user') {
    return { url: '/user/search-booking', method: 'post' }
  }
  if (type.value === 'guest') {
    return { url: '/guest/search-booking', method: 'post' }
  }
})

// Booking form
const form = reactive({
  guestId: '',
  shipmentType: 'export',
  transportation: 'CONTAINER',
  containerType: '',
  vehicleCategory: '',
  goodsType: '',
  origin: null,
  originName: '',
  originInfo: {
    address: '',
    addressDetail: '',
    locationName: '',
    placeId: '',
    latitudeAndLongitude: '',
    lat: 0,
    lng: 0
  },
  destination: null,
  destinationName: '',
  destinationInfo: {
    address: '',
    addressDetail: '',
    locationName: '',
    placeId: '',
    latitudeAndLongitude: '',
    lat: 0,
    lng: 0
  },
  date: ''
})

// Helpers
const getLabel = (list: { value: string | number; label: string }[], value: string | number): string => {
  const found = list.find((item) => item.value === value)
  return found ? found.label : ''
}

function getVehicleType (item: any) {
  if (item.transportation === 'TRUCK') {
    return item.vehicleCategory || 'Không rõ'
  }
  if (item.transportation === 'CONTAINER') {
    return item.containerType || 'Không rõ'
  }
  return item.transportation
}

function locationChange (type: string, datas: any) {
  if (!Array.isArray(datas) || !datas.length) {
    return
  }
  const data = datas[0]
  const target = {
    address: data.address,
    addressDetail: data.addressDetail,
    locationName: data.locationName,
    placeId: data.placeId,
    latitudeAndLongitude: `${data.lat}, ${data.lng}`,
    lat: data.lat,
    lng: data.lng
  }
  if (type === 'originInfo') {
    form.originInfo = target
  } else if (type === 'destinationInfo') {
    form.destinationInfo = target
  }
}

function selectSearchHistory (item: any) {
  Object.assign(form, { guestId: store.guest.getGuestId, ...(item || {}) })
  handleSearch()
}

function handleSearch () {
  if (!apiSearchBooking.value) {
    return
  }
  loadingSearch.value = true
  form.guestId = store.guest.getGuestId

  $api(apiSearchBooking.value, { body: form })
    .then((data: any) => {
      const resData = data?.data?.value
      if (resData?.success && Array.isArray(resData.data)) {
        resultPrices.value = resData.data
          .flatMap((result: any) =>
            result?.priceData?.prices.map((price: any) => {
              if (!price) {
                return null
              }
              return {
                origin: form.originInfo,
                originName: form.originName,
                destination: form.destinationInfo,
                destinationName: form.destinationName,
                shipmentType: form.shipmentType,
                transportation: form.transportation,
                containerType: form.containerType,
                vehicleCategory: form.vehicleCategory,
                date: price.date,
                price: price.pricingEstimate.selling,
                distance: price.routingPositions?.route?.distance,
                tasks: price.positions.flatMap((pos: any) => pos.tasks || []),
                searchResultId: result.searchResultId,
                routingPositions: price.routingPositions,
                assumption: price.assumption
              }
            })
          )
          .filter((item: any): item is NonNullable<typeof item> => item !== null)
      } else {
        resultPrices.value = []
      }
      loadingSearch.value = false
    })
    .catch(() => {
      resultPrices.value = null
      loadingSearch.value = false
    })
}

function openBookingDrawer (price: any) {
  bookingDrawer.value?.openBookingDrawer(price)
}

function getDataGuest (id: string) {
  const api = { url: `guest/auth/${id}`, method: 'get' }
  $api(api).then((data: any) => {
    const resData = data?.data?.value
    if (resData?.success) {
      store.guest.setGuestInfo(resData.data.guestId ? resData.data : { guestId: queryGuestId.value })
      getRecentSearches(resData.data.guestId || queryGuestId.value)
    }
  })
}

function getRecentSearches (id: string) {
  const api = { url: `guest/input-booking/${id}`, method: 'get' }
  $api(api).then((data: any) => {
    const resData = data?.data?.value
    if (resData?.success) {
      recentSearches.value = resData.data || []
    }
  })
}

// Iframe resize auto
const sendHeightToParent = $lodash.debounce(() => {
  const height = document.documentElement.scrollHeight
  window.parent.postMessage({ type: 'setIframeHeight', height }, '*')
}, 200)

onMounted(() => {
  form.date = dayjs().format('YYYY-MM-DD')

  setTimeout(sendHeightToParent, 200)
  observer = new ResizeObserver(sendHeightToParent)
  observer.observe(document.body)

  if (type.value === 'guest') {
    if (queryGuestId.value) {
      getDataGuest(queryGuestId.value)
    } else {
      const newId = crypto.randomUUID()
      store.guest.setGuestId(newId)
      getRecentSearches(newId)
    }
  }
})

onBeforeUnmount(() => {
  observer?.disconnect()
})
</script>

<style scoped>
/**** Hero Section Gradient & Animation ****/
section.bg-gradient-to-r {
  background: linear-gradient(to right, #e0f7ff, #f9fbff);
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fade-in 0.8s ease-out both;
}
</style>
